import { useContext } from "react";
import { AuthContext } from "./AuthContext";

/**
 * Hook to access auth context.
 * Must be used within an AuthProvider.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Convenience hook to check if current user is an admin.
 */
export function useIsAdmin() {
  const { user } = useAuth();
  return user?.role === "admin";
}

// Mirrors backend Permit.TARGETS_MANAGE in apps/api/app/permissions.py.
export function useCanManageTargets() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Mirrors backend Permit.TARGETS_READ: managers edit, sales reps read the
// published plan. CSRs and other roles have no targets access at all.
export function useCanReadTargets() {
  const { user } = useAuth();
  return (
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "sales_rep"
  );
}

// Mirrors backend Permit.FORECASTING_ALL, which gates /views/plan-grid and
// /forecast-cycles. CSRs hold FORECAST_MY_PIPELINE but NOT this, so they can
// work their own book while the company forecast grid and the cycle look-back
// stay closed to them.
export function useCanReadForecasting() {
  const { user } = useAuth();
  return (
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "sales_rep"
  );
}

/**
 * Issue access tiers. Everyone signed in is on the report tier (raise
 * issues, see the Report tab + workflow metadata). Managers and admins
 * are additionally on the investigate tier: they see investigation and
 * resolution content and can work issues through to closure.
 */
export function useCanInvestigateIssues() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Dev sessions (feature-sessions) area — the "techie" tier. Mirrors the
// backend Permit.FEATURE_SESSIONS_USE (admin + manager). The backend permit is
// the real gate; this hook only decides nav/affordance visibility.
export function useCanManageFeatureSessions() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Ticket category taxonomy curation. Mirrors backend
// Permit.WORKFLOW_MANAGE_CATEGORIES (admin + manager). The backend permit is
// the real gate; this hook only decides whether to show the manage affordances.
export function useCanManageTicketCategories() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// CSR buddy-list curation (csr-ticket-routing). Admin + manager, per the
// product brief's confirmed decision — CSRs don't edit their own or
// others' lists. The backend permit is the real gate; this hook only
// decides whether to show the manage affordances.
export function useCanManageBuddyLists() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Setup's Automatic actions: which proposed actions One does on its own. Mirrors backend
// Permit.EMAILS_AUTOMATIC_ACTIONS (admin + manager, Ben 27 Oct). The backend permit is the real gate; this hook only
// decides whether the Setup line is drawn.
export function useCanManageAutomaticActions() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Ticket covering assignment (csr-ticket-routing V2) and other "touch a
// ticket you're not the assignee of" actions. Mirrors backend
// Permit.WORKFLOW_MANAGE_ANY (admin + manager). The backend permit is the
// real gate; this hook only decides whether to show the manage affordances.
export function useCanManageAnyTicket() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Tharstern-held job documents. Mirrors backend Permit.THARSTERN_ATTACHMENTS,
// which is granted exactly where Permit.THARSTERN_JOBS is (admin, manager,
// sales_rep, csr, marketing) — deliberately NOT to commercial/editor, who hold
// WORKFLOW_ALL but cannot read Tharstern jobs.
//
// The backend permit is the real gate. This hook exists so the section is
// absent rather than broken for those roles: rendered unguarded, the list 403s
// and the component reports "Couldn't load documents from Tharstern", which
// reads as a vendor outage and would generate false outage reports.
export function useCanUseTharsternAttachments() {
  const { user } = useAuth();
  const role = user?.role;
  return (
    role === "admin" ||
    role === "manager" ||
    role === "sales_rep" ||
    role === "csr" ||
    role === "marketing"
  );
}

// Creating a customer in CIS, on every way in (v1 completion C5): the
// Customers page, the catalogue's dialog, a won lead and Promo Office.
// Mirrors backend Permit.CUSTOMERS_EDIT (admin, manager, sales_rep, csr,
// marketing).
//
// The backend permit is the real gate. This hook only decides whether to offer
// the button — offered unguarded it 403s, which reads as a broken page rather
// than as "not your job".
export function useCanEditCustomers() {
  const { user } = useAuth();
  const role = user?.role;
  return (
    role === "admin" ||
    role === "manager" ||
    role === "sales_rep" ||
    role === "csr" ||
    role === "marketing"
  );
}

// Deciding which CIS supplier a Promo Office supplier name is (merchandise
// spec 11 §8): a supplier link decides who a real purchase order goes to, as
// an order's *Change supplier…* does. Mirrors backend Permit.ORDERS_MANAGE
// (admin, manager, sales_rep, csr, marketing). The backend permit is the real
// gate; this only keeps the decision buttons off a page where they would 403.
export function useCanManageOrders() {
  const { user } = useAuth();
  const role = user?.role;
  return (
    role === "admin" ||
    role === "manager" ||
    role === "sales_rep" ||
    role === "csr" ||
    role === "marketing"
  );
}

// Feature state view. Mirrors backend Permit.FEATURES_READ
// (admin + engineer) — the page shows allowlist membership and every
// unreleased feature name, which is exactly what /me withholds from ordinary
// users. The backend permit is the real gate; this hook only decides whether
// to show the nav entry.
export function useCanReadFeatures() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "engineer";
}

// Catalogue curation — edit mode, create/constrain items, and bulk import.
// Curators are admin + manager (the option library / pricing admin layer sits
// behind this). The backend permit is the real gate; this hook only decides
// whether to show the manage affordances.
export function useCanCurateCatalogue() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Manufacturing setup (docs/features/cost-engine-productionisation/
// 03_manufacturing_surfaces_design.md) — read-only for admin, engineer and
// manager (product brief §3: admins own it, engineers work sync coverage
// and decode rounds, managers get read-only visibility; sales/CSR/estimators
// never see it). No write affordances exist on this surface yet, so this
// hook gates visibility only, same as the others on this page.
export function useCanReadManufacturingSetup() {
  const { user } = useAuth();
  return (
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "engineer"
  );
}

// Accounts' month-end accrual/deferral upload. Mirrors backend
// Permit.FORECASTING_ACCRUALS (admin + manager) — the coarse permit is the
// whole authorisation story on that surface, there is no portfolio scoping.
// The backend permit is the real gate; this hook only decides whether to show
// the entry point and the page's own content.
export function useCanUploadAccruals() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Payment refunds (payments_module). Mirrors backend Permit.PAYMENTS_MANAGE
// (admin + manager) — the same tier as every other money-adjacent staff
// action in this codebase. The backend permit is the real gate; this hook
// only decides whether to show the refund action on the payment panel.
export function useCanManagePayments() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Mirrors backend Permit.CREDIT_CONTROL_MANAGE (admin, manager, finance): importing
// billing accounts from the Credit control list. The composed credit reads
// carry their own `canManage`; the list has no per-row field to carry it, so
// the page asks here. The backend permit is the real gate.
export function useCanManageCreditControl() {
  const { user } = useAuth();
  return (
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "finance"
  );
}

// Mirrors backend Permit.ORDERS_RELEASE (admin, manager, csr, marketing): who may
// set an order's release policy — the "Review before release" and "Deliver
// together" switches. The order page carries this per-switch as a `set_url`; the
// checkout dialog's switches have no such field, so it asks here whether to show
// them. A sales_rep may place an order (CATALOGUE_ORDER) but not decide its
// release policy, so the switches stay hidden. The backend route is the real gate
// (it drops any override from a caller without this permit).
export function useCanReleaseOrders() {
  const { user } = useAuth();
  return (
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "csr" ||
    user?.role === "marketing"
  );
}

// Marketing-owned reads (campaigns, audiences, contacts, and the composed
// views that carry campaign names). Mirrors backend Permit.MARKETING_ENGINE_READ
// (admin, manager, sales_rep, csr, marketing) — deliberately NOT commercial or
// engineer.
//
// The backend permit is the real gate. This hook exists so a marketing surface
// is absent rather than broken for those roles: the Insights → Campaign Impact
// tab rendered unguarded 403s and reports "Couldn't load campaign impact",
// which reads as an outage rather than as "not for you".
export function useCanReadMarketing() {
  const { user } = useAuth();
  const role = user?.role;
  return (
    role === "admin" ||
    role === "manager" ||
    role === "sales_rep" ||
    role === "csr" ||
    role === "marketing"
  );
}

// Marketing writes (campaigns, triggers, costs, plan refs, performance targets). Mirrors backend
// Permit.MARKETING_ENGINE_ALL (admin, manager, marketing). The backend permit
// is the real gate; this hook only decides whether to show the edit controls,
// so read-only roles see the page without dead buttons.
export function useCanManageMarketing() {
  const { user } = useAuth();
  const role = user?.role;
  return role === "admin" || role === "manager" || role === "marketing";
}

// Department admin CRUD (department-ticket-routing) — create/rename/set-
// lead/edit-members/retire. Mirrors backend Permit.USERS_MANAGE_DEPARTMENTS
// (admin + manager), the same tier as ticket categories and buddy lists.
// The backend permit is the real gate; this hook only decides whether to
// show the settings entry and the page's manage affordances.
export function useCanManageDepartments() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

// Email-capture sender/domain denylist admin (email-capture-exceptions).
// Mirrors backend Permit.CUSTOMER_COMMUNICATIONS_MANAGE_EXCLUSIONS (admin +
// manager) — deliberately its own permit, not the general comms read/write
// ones, because this changes what the system records for everyone. The
// backend permit is the real gate; this hook only decides whether to show
// the settings entry and the page's manage affordances.
export function useCanManageEmailCaptureExclusions() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}

/**
 * Insights tab visibility, by tab family. Mirrors the backend
 * Permit.INSIGHTS_COMMERCIAL / _MANAGEMENT / _FINANCE
 * (apps/api/app/permissions.py). The backend permit on each route is the real
 * gate; this hook only decides which tabs to render, so a user is never shown
 * a tab that would 403 when it loaded.
 *
 * commercial — revenue, mix, customers, carriage recovery (admin, manager,
 * commercial). management — the operational/reconciliation tabs (admin +
 * manager). finance — P&L-adjacent numbers (admin, manager, finance).
 */
export function useCanViewInsightsTab(
  family: "commercial" | "management" | "finance",
) {
  const { user } = useAuth();
  if (user?.role === "admin" || user?.role === "manager") return true;
  if (family === "commercial") return user?.role === "commercial";
  if (family === "finance") return user?.role === "finance";
  return false;
}

// Staff view-as / preview of the customer portal (customer-portal-view-as).
// Mirrors backend Permit.CUSTOMER_PORTAL_VIEW_AS: every signed-in role except pending
// ("everyone sees everyone", 14 Oct 2026). Permissions are by role only; there are no
// per-person grants (docs/features/customer-portal-view-as/09_staff_powers_in_the_portal.md
// §2.2). Gates the "View as" entry on SettingsCustomerPortalPage's PersonRow menu and
// "Preview portal" on CustomerDetailPage. The backend permit at get_staff_portal_view_access
// is the real gate; this hook only decides whether to show the entry points.
export function useCanViewCustomerPortalAs() {
  const { user } = useAuth();
  return user != null && user.role !== "pending";
}

// Staff "act for" the customer -- view-as of a real person with writes
// switched on. Mirrors backend Permit.CUSTOMER_PORTAL_ACT_FOR (admin, manager, csr,
// marketing, sales_rep; see docs/features/customer-portal-view-as/07_act_for_amendment.md
// and, for sales_rep, the 15 Oct 2026 roles-only decision). Gates
// the "Act for ..." switch in the staff-view shell (person targets only --
// the switch itself is never shown for a preview target, so this hook is
// necessary but not sufficient: StaffViewLayout also checks the target's
// mode). The backend permit at get_staff_portal_act_for_access is the real
// gate; this hook only decides whether to show the entry point.
export function useCanActForCustomer() {
  const { user } = useAuth();
  return (
    user?.role === "admin" ||
    user?.role === "manager" ||
    user?.role === "csr" ||
    user?.role === "marketing" ||
    user?.role === "sales_rep"
  );
}

/**
 * Stock control (stock_module). Mirrors backend Permit.STOCK_READ (every
 * operational staff role; not engineer, finance or pending) and Permit.STOCK_MANAGE
 * (stock_control + admin). The backend permits are the real gate; these
 * only decide what to render.
 */
const STOCK_READ_ROLES = new Set([
  "admin",
  "manager",
  "sales_rep",
  "csr",
  "commercial",
  "marketing",
  "stock_control",
]);

// Mirrors backend Permit.JOURNEY_TESTING_USE (apps/api/app/permissions.py): the test console's roles.
const JOURNEY_TESTING_ROLES = new Set(["admin", "manager", "csr", "finance"]);

/** The test console (a testing preview only): shown to the roles holding journey_testing:use. */
export function useCanUseJourneyTesting() {
  const { user } = useAuth();
  return user != null && JOURNEY_TESTING_ROLES.has(user.role);
}

export function useCanViewStock() {
  const { user } = useAuth();
  return user != null && STOCK_READ_ROLES.has(user.role);
}

export function useCanManageStock() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "stock_control";
}

/**
 * Stock replenishment (BrandHub doc 09 §8). Mirrors backend
 * Permit.REPLENISHMENT_VIEW and REPLENISHMENT_MANAGE, which the same five
 * roles hold: seeing policies and proposals, and setting a policy, confirming,
 * dismissing and replenishing now.
 */
const REPLENISHMENT_ROLES = new Set([
  "admin",
  "manager",
  "csr",
  "marketing",
  "stock_control",
]);

export function useCanViewReplenishment() {
  const { user } = useAuth();
  return user != null && REPLENISHMENT_ROLES.has(user.role);
}

export function useCanManageReplenishment() {
  return useCanViewReplenishment();
}

// CSR account allocation (docs/features/csr-account-allocation/) — mirrors
// backend Permit.ACCOUNT_ALLOCATION_MANAGE (admin + manager). Gates the
// "Account allocation" nav entry and the queue/claims-management view —
// the self-service claim surface (ACCOUNT_ALLOCATION_CLAIM) is open to csr
// too and doesn't need a hook since every signed-in user can reach it from
// their own customer pages.
export function useCanManageAccountAllocation() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager";
}
